import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Link,
  Paper,
  TextField,
  Typography
} from '@material-ui/core'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import dayjs from 'dayjs'
import React from 'react'
import { useParams } from 'react-router-dom'

import { fetchJoinerUpload, signOffer } from 'api/newJoiners'

import SignaturePad from '../Operations/components/SignaturePad'

/**
 * THE NEW JOINER'S PAGE. No login: they have no Alfred account yet. The token
 * in the URL (from the offer email) is the authorisation.
 *
 * They read their offer letter here and sign it online (user, 2026-10-28):
 * type their full name, draw their signature, tick "I accept". Bella stamps
 * the signature into the letter's Acceptance section and keeps the signed
 * PDF; signing unlocks the pre-joining email. Once signed the link only shows
 * the signed copy — it can't be signed again.
 */
const errorDetail = (e) =>
  e?.response?.data?.detail || 'Something went wrong. Please try again.'

const NewJoinerSign = () => {
  const { token } = useParams()
  const [info, setInfo] = React.useState(null)
  const [loadError, setLoadError] = React.useState('')
  const [fullName, setFullName] = React.useState('')
  const [signature, setSignature] = React.useState('')
  const [accepted, setAccepted] = React.useState(false)
  const [error, setError] = React.useState('')
  const [signing, setSigning] = React.useState(false)

  React.useEffect(() => {
    fetchJoinerUpload(token)
      .then((data) => {
        setInfo(data)
        setFullName(data.name || '')
      })
      .catch((e) => setLoadError(errorDetail(e)))
  }, [token])

  const submit = async () => {
    if (fullName.trim().length < 2) {
      setError('Type your full name.')
      return
    }
    if (!signature) {
      setError('Draw your signature in the box.')
      return
    }
    if (!accepted) {
      setError('Tick the box to confirm you accept the offer.')
      return
    }
    setSigning(true)
    setError('')
    try {
      setInfo(
        await signOffer(token, {
          full_name: fullName.trim(),
          signature,
          accept: true
        })
      )
    } catch (e) {
      setError(errorDetail(e))
    } finally {
      setSigning(false)
    }
  }

  let content
  if (!info && !loadError) {
    content = (
      <Box textAlign="center" py={4}>
        <CircularProgress />
      </Box>
    )
  } else if (!info) {
    content = <Typography color="error">{loadError}</Typography>
  } else if (info.signed) {
    content = (
      <>
        <Box display="flex" alignItems="center" mb={2} style={{ gap: 10 }}>
          <CheckCircleIcon style={{ color: '#2e7d32', fontSize: 32 }} />
          <Typography variant="h6">
            Thank you, {info.first_name}. Your offer letter is signed.
          </Typography>
        </Box>
        <Typography paragraph>
          You signed it on {dayjs(info.signed_at).format('D MMMM YYYY, h:mm A')}
          . We look forward to welcoming you to Openhouse on {info.joining_date}
          . We&apos;ll email you the details for your first day two days before
          you join.
        </Typography>
        {info.signed_copy_url && (
          <Link
            href={info.signed_copy_url}
            target="_blank"
            rel="noopener noreferrer"
          >
            Download your signed copy
          </Link>
        )}
      </>
    )
  } else if (info.closed) {
    content = <Typography>This link has expired. Please contact HR.</Typography>
  } else {
    content = (
      <>
        <Typography variant="h6" gutterBottom>
          Hi {info.first_name}, welcome to Openhouse
        </Typography>
        <Typography variant="body2" color="textSecondary" paragraph>
          {info.designation} · {info.centre} · joining {info.joining_date}
        </Typography>
        <Typography paragraph>
          Please read your offer letter below, then sign it at the bottom of
          this page.
        </Typography>

        <iframe
          title="Your offer letter"
          sandbox=""
          srcDoc={info.letter_html || ''}
          style={{
            width: '100%',
            height: '60vh',
            border: '1px solid #e0e0e0',
            borderRadius: 4,
            background: '#fff'
          }}
        />

        <Typography
          variant="subtitle1"
          style={{ marginTop: 24, fontWeight: 600 }}
        >
          Sign your offer letter
        </Typography>
        <TextField
          id="joiner-full-name"
          label="Your full name"
          value={fullName}
          onChange={(e) => {
            setFullName(e.target.value)
            setError('')
          }}
          fullWidth
          margin="dense"
        />
        <Typography
          variant="body2"
          color="textSecondary"
          style={{ marginTop: 12, marginBottom: 4 }}
        >
          Draw your signature
        </Typography>
        <SignaturePad
          label=""
          value={signature}
          onChange={(v) => {
            setSignature(v)
            setError('')
          }}
        />
        <FormControlLabel
          style={{ marginTop: 8 }}
          control={
            <Checkbox
              id="joiner-accept"
              checked={accepted}
              onChange={(e) => {
                setAccepted(e.target.checked)
                setError('')
              }}
              color="primary"
            />
          }
          label="I have read and accept the terms of this offer letter."
        />
        {error && (
          <Typography color="error" variant="body2" style={{ marginTop: 4 }}>
            {error}
          </Typography>
        )}
        <Box mt={2}>
          <Button
            color="primary"
            variant="contained"
            onClick={submit}
            disabled={signing}
          >
            {signing ? 'Signing…' : 'Sign and accept'}
          </Button>
        </Box>
      </>
    )
  }

  return (
    <Box
      minHeight="100vh"
      display="flex"
      alignItems="flex-start"
      justifyContent="center"
      p={2}
      style={{ background: '#f5f5f5' }}
    >
      <Paper
        style={{ padding: 24, maxWidth: 820, width: '100%', marginTop: 32 }}
      >
        {content}
      </Paper>
    </Box>
  )
}

export default NewJoinerSign
